import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box, Typography, Grid, Card, Chip, Button, LinearProgress,
  CircularProgress, Alert, Divider, IconButton, Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import PauseCircleOutlinedIcon from "@mui/icons-material/PauseCircleOutlined";
import PlayCircleOutlinedIcon from "@mui/icons-material/PlayCircleOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { API_BASE_URL } from "../../config";
import { needsLicenceFile } from "../../utils/tenantHosting";

const CLOUD_COLOR = { aws: "#FF9900", gcp: "#4285F4", azure: "#0089D6", private: "#757575" };
const TIER_COLOR  = { tenant: "#1976d2", enterprise: "#7b1fa2", multitenant: "#00838f" };

function InfoRow({ label, value }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", py: 1, borderBottom: "1px solid #f0f0f0" }}>
      <Typography fontSize="13px" color="text.secondary">{label}</Typography>
      <Typography fontSize="13px" fontWeight={500}>{value || "—"}</Typography>
    </Box>
  );
}

function SectionCard({ title, children }) {
  return (
    <Card elevation={0} sx={{ border: "1px solid #e8e8e8", borderRadius: 2, p: 3, bgcolor: "#fff" }}>
      <Typography fontWeight={700} fontSize="14px" color="#344054" mb={2}>{title}</Typography>
      {children}
    </Card>
  );
}

export default function TenantDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/superadmin/tenants/${id}`);
      setTenant(res.data.tenant);
    } catch {
      setError("Failed to load tenant details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const handleToggleStatus = async () => {
    const newStatus = tenant.status === "active" ? "suspended" : "active";
    try {
      await axios.put(`${API_BASE_URL}/superadmin/tenants/${tenant.id}/status`, { status: newStatus });
      load();
    } catch { setError("Failed to update status."); }
  };

  const handleDownloadLic = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/superadmin/tenants/${tenant.id}/licence-key`);
      const blob = new Blob([res.data.licenceKey], { type: "text/plain" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = `${tenant.slug}.lic`; a.click();
      URL.revokeObjectURL(url);
    } catch { setError("Failed to download licence key."); }
  };

  const handleRegenerateLic = async () => {
    if (!window.confirm(`Regenerate .lic for ${tenant.org_name} and email ${tenant.admin_email}?`)) return;
    try {
      const res = await axios.post(`${API_BASE_URL}/superadmin/tenants/${tenant.id}/licence/regenerate`);
      const key = res.data?.licenceKey;
      if (key) {
        const blob = new Blob([key], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `${tenant.slug}.lic`; a.click();
        URL.revokeObjectURL(url);
      }
      setError("");
      if (res.data?.licenceEmailSent === false) {
        setError("Licence regenerated (download started). Email was not sent — check SMTP.");
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to regenerate licence.");
    }
  };

  const handleDownloadDeployment = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/superadmin/tenants/${tenant.id}/deployment`);
      const { env, deployScript, tenant: t } = res.data;
      // Download .env
      const envBlob = new Blob([env], { type: "text/plain" });
      const envUrl  = URL.createObjectURL(envBlob);
      const a1 = document.createElement("a"); a1.href = envUrl; a1.download = `.env.${t.slug}`; a1.click();
      URL.revokeObjectURL(envUrl);
      // Download deploy.sh
      const shBlob = new Blob([deployScript], { type: "text/plain" });
      const shUrl  = URL.createObjectURL(shBlob);
      const a2 = document.createElement("a"); a2.href = shUrl; a2.download = `deploy-${t.slug}.sh`; a2.click();
      URL.revokeObjectURL(shUrl);
    } catch { setError("Failed to generate deployment package."); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Permanently delete ${tenant.org_name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_BASE_URL}/superadmin/tenants/${tenant.id}`);
      navigate("/platform/tenants");
    } catch { setError("Failed to delete tenant."); }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
      <CircularProgress />
    </Box>
  );

  if (!tenant) return (
    <Box sx={{ p: 4 }}><Alert severity="error">{error || "Tenant not found."}</Alert></Box>
  );

  const usedPct = tenant.seat_count > 0 ? Math.min(100, Math.round((tenant.seats_used / tenant.seat_count) * 100)) : 0;
  const overWarn = usedPct >= (tenant.warning_threshold || 80);
  const cloudColor = CLOUD_COLOR[tenant.cloud] || "#757575";
  const tierColor  = TIER_COLOR[tenant.tier]   || "#1976d2";
  const daysLeft   = tenant.contract_end
    ? Math.ceil((new Date(tenant.contract_end) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <Box sx={{ bgcolor: "#f9fafb", px: 4, py: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton onClick={() => navigate("/platform/tenants")} size="small"
            sx={{ border: "1px solid #D9D9D9", borderRadius: 1 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight={700}>{tenant.org_name}</Typography>
            <Typography variant="body2" color="text.secondary">{tenant.slug}</Typography>
          </Box>
          <Chip label={tenant.cloud.toUpperCase()} size="small"
            sx={{ bgcolor: cloudColor + "1A", color: cloudColor, border: `1px solid ${cloudColor}44`, fontWeight: 700 }} />
          <Chip label={tenant.tier.charAt(0).toUpperCase() + tenant.tier.slice(1)} size="small"
            sx={{ bgcolor: tierColor + "1A", color: tierColor, border: `1px solid ${tierColor}44`, fontWeight: 700 }} />
          <Chip
            label={tenant.status.charAt(0).toUpperCase() + tenant.status.slice(1)}
            color={{ active: "success", suspended: "error", expired: "warning", pending: "default" }[tenant.status]}
            size="small" sx={{ fontWeight: 700 }}
          />
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Extend Licence">
            <Button size="small" variant="outlined" startIcon={<ExtensionOutlinedIcon />}
              onClick={() => navigate("/platform/tenants")}
              sx={{ textTransform: "none", color: "#0094FB", borderColor: "#0094FB" }}>
              Extend
            </Button>
          </Tooltip>
          <Tooltip title={tenant.status === "active" ? "Suspend Tenant" : "Reactivate Tenant"}>
            <Button size="small" variant="outlined" onClick={handleToggleStatus}
              startIcon={tenant.status === "active" ? <PauseCircleOutlinedIcon /> : <PlayCircleOutlinedIcon />}
              sx={{ textTransform: "none",
                color: tenant.status === "active" ? "#ed6c02" : "#2e7d32",
                borderColor: tenant.status === "active" ? "#ed6c02" : "#2e7d32" }}>
              {tenant.status === "active" ? "Suspend" : "Activate"}
            </Button>
          </Tooltip>
          <Tooltip title="Download Deployment Package">
            <Button size="small" variant="outlined" startIcon={<DownloadOutlinedIcon />}
              onClick={handleDownloadDeployment}
              sx={{ textTransform: "none", color: "#344054", borderColor: "#D9D9D9" }}>
              Deploy
            </Button>
          </Tooltip>
          {needsLicenceFile(tenant) && (
            <>
              <Tooltip title="Download .lic Key">
                <Button size="small" variant="outlined" startIcon={<VpnKeyOutlinedIcon />}
                  onClick={handleDownloadLic}
                  sx={{ textTransform: "none", color: "#1976d2", borderColor: "#1976d2" }}>
                  .lic Key
                </Button>
              </Tooltip>
              <Tooltip title="Regenerate .lic and email tenant admin">
                <Button size="small" variant="outlined" startIcon={<RefreshIcon />}
                  onClick={handleRegenerateLic}
                  sx={{ textTransform: "none", color: "#0e7c7b", borderColor: "#0e7c7b" }}>
                  Regenerate
                </Button>
              </Tooltip>
            </>
          )}
          <Tooltip title="Delete Tenant">
            <Button size="small" variant="outlined" startIcon={<DeleteOutlineIcon />}
              onClick={handleDelete}
              sx={{ textTransform: "none", color: "#d32f2f", borderColor: "#d32f2f" }}>
              Delete
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Seat usage banner */}
      <Card elevation={0} sx={{ border: `1px solid ${overWarn ? "#ed6c02" : "#e8e8e8"}`, borderRadius: 2, p: 2.5, mb: 3, bgcolor: overWarn ? "#fff8f0" : "#fff" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography fontWeight={600} fontSize="14px">
            Licence Usage — {usedPct}% used
          </Typography>
          <Typography fontSize="13px" color="text.secondary">
            {tenant.seats_used} / {tenant.seat_count} seats
          </Typography>
        </Box>
        <LinearProgress variant="determinate" value={usedPct}
          sx={{ height: 8, borderRadius: 4, "& .MuiLinearProgress-bar": { bgcolor: overWarn ? "#ed6c02" : "#1976d2" } }} />
        {overWarn && (
          <Typography fontSize="12px" color="warning.main" mt={1}>
            Seat usage above {tenant.warning_threshold}% threshold. Consider extending the licence.
          </Typography>
        )}
      </Card>

      {/* Detail cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <SectionCard title="Organisation Details">
            <InfoRow label="Organisation Name" value={tenant.org_name} />
            <InfoRow label="Realm Slug" value={tenant.slug} />
            <InfoRow label="Hosting" value={(tenant.hosting || (tenant.cloud === "private" ? "dedicated" : "shared"))?.toUpperCase()} />
            <InfoRow label="Cloud Provider" value={tenant.cloud?.toUpperCase()} />
            <InfoRow label="Region" value={tenant.region} />
            <InfoRow label="Industry" value={tenant.industry} />
            <InfoRow label="Created By" value={tenant.created_by} />
            <InfoRow label="Created At" value={tenant.created_at ? new Date(tenant.created_at).toLocaleString() : null} />
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="Authentication">
            <InfoRow label="Auth Mode" value={tenant.auth_mode} />
            <InfoRow label="MFA" value={tenant.mfa_enabled ? "Enabled" : "Disabled"} />
            <InfoRow label="Force Password Change" value={tenant.force_password_change ? "Yes" : "No"} />
            <InfoRow label="Session Timeout" value={`${tenant.session_timeout_mins} minutes`} />
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="Licence">
            <InfoRow label="Tier" value={tenant.tier?.charAt(0).toUpperCase() + tenant.tier?.slice(1)} />
            <InfoRow label="Seats" value={`${tenant.seat_count} total`} />
            <InfoRow label="Warning Threshold" value={`${tenant.warning_threshold}%`} />
            <InfoRow label="Contract Start" value={tenant.contract_start ? new Date(tenant.contract_start).toLocaleDateString() : null} />
            <InfoRow label="Contract End" value={tenant.contract_end ? new Date(tenant.contract_end).toLocaleDateString() : null} />
            <InfoRow label="Days Remaining"
              value={daysLeft !== null ? (daysLeft < 0 ? "Expired" : `${daysLeft} days`) : null} />
            <InfoRow label="On Expiry" value={tenant.expiry_behaviour} />
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="Client Admin">
            <InfoRow label="Name" value={`${tenant.admin_first_name || ""} ${tenant.admin_last_name || ""}`.trim()} />
            <InfoRow label="Email" value={tenant.admin_email} />
            <InfoRow label="Username" value={tenant.admin_username} />
            <Divider sx={{ my: 2 }} />
            <Typography fontSize="12px" color="text.secondary">
              Login URL for this tenant:
            </Typography>
            <Typography fontSize="12px" fontFamily="monospace" sx={{ wordBreak: "break-all", mt: 0.5, color: "#0094FB" }}>
              {window.location.origin}?realm={tenant.slug}
            </Typography>
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
}
